import { getMilestoneLabel } from '@/helpers/getMilestoneLabel';
import { intercomTarget } from '@/helpers/intercomTarget';
import { Alert, Badge, Box, Card, Spinner } from '@palmetto/palmetto-components';
import { ReactNode, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AccountStipulationType, Milestone, MilestoneRequirement, MilestoneStatus, ProgramType } from 'types';

import { useGetAccountQuery, useGetAccountStipulationsQuery } from '../../services/accounts';
import ProjectDates from '../Account/ProjectDates';
import RequirePalmettoFinanceUser from '../auth/RequirePalmettoFinanceUser';
import DateTimeDisplay from '../DateTime';
import { StatusIcon } from '../StatusIcon';

import PauseBanner from './PauseBanner';

const isConditionalRequirementSubmittedOrResubmitted = (requirement: any) => {
  const requirements = [MilestoneRequirement.domesticContentConfirmation];
  return (
    (requirement.status === MilestoneStatus.resubmitted || requirement.status === MilestoneStatus.submitted) &&
    requirements.includes(requirement.type)
  );
};

const filterOptionalPendingRequirements = (requirement: any) => {
  return (
    requirement.isRequired ||
    (!requirement.isRequired &&
      (requirement.status === 'rejected' ||
        requirement.status === 'completed' ||
        isConditionalRequirementSubmittedOrResubmitted(requirement)))
  );
};

const SubTask = ({
  children,
  className,
  intercomTargetKey,
  status,
}: {
  children: ReactNode;
  className?: string;
  intercomTargetKey?: string;
  status?: 'completed' | 'inProgress' | 'rejected' | 'pending';
}) => (
  <Box as="li" position="relative" className={className}>
    <Box direction="row" childGap="xs" alignItems="flex-start" position="relative">
      <Box background="primary">
        <StatusIcon status={status} size="md" />
      </Box>
      <Box data-intercom-target={intercomTargetKey}>{children}</Box>
    </Box>
  </Box>
);

export function AccountMilestones({ className }: { className?: string }) {
  const { id } = useParams<{ id: any }>();
  const { data: account, isLoading } = useGetAccountQuery(id);
  const { data: stipulations } = useGetAccountStipulationsQuery(id, { skip: !id });

  const [showDetails, setShowDetails] = useState(true);
  const programType = account?.programType;
  const accountIsHvac = programType === ProgramType.hvac;
  const accountIsWaterHeater = programType === ProgramType.waterHeater;
  const accountIsGenerator = programType === ProgramType.generator;
  const sortedMilestones = useMemo(() => {
    if (account?.milestones) {
      const milestoneCopy = [...account.milestones];
      return milestoneCopy.toSorted((a: any, b: any) => a.order - b.order);
    } else {
      return [];
    }
  }, [account]);

  const { milestonePausingStipulations, hasUnsatisfiedPausingStipulations } = useMemo(() => {
    if (!stipulations) {
      return {};
    }
    return {
      milestonePausingStipulations: stipulations.filter(
        (stipulation) =>
          (stipulation.stipulationType === AccountStipulationType.designVerification ||
            stipulation.stipulationType === AccountStipulationType.changeOrder) &&
          !stipulation.isSatisfied,
      ),
      hasUnsatisfiedPausingStipulations: stipulations.some(
        (stipulation) =>
          (stipulation.stipulationType === AccountStipulationType.administrative ||
            stipulation.stipulationType === AccountStipulationType.designVerification ||
            stipulation.stipulationType === AccountStipulationType.changeOrder) &&
          !stipulation.isSatisfied,
      ),
    };
  }, [stipulations]);

  const milestoneStatuses = useMemo(() => {
    return account?.milestones?.reduce((acc: any, milestone: any) => {
      const hasSomeCompleted = milestone?.requirements?.some((req: any) => req.status === 'completed');
      let status;
      switch (milestone?.status) {
        case 'rejected':
        case 'paused':
        case 'submitted':
        case 'resubmitted':
          status = milestone.status;
          break;
        case 'approved':
          status = 'completed';
          break;
        default:
          status = hasSomeCompleted ? 'inProgress' : undefined;
      }
      acc[milestone.type] = status;
      return acc;
    }, {});
  }, [account]);

  const milestoneLabels = useMemo(() => {
    return (account?.milestones as Milestone[])?.reduce((acc: Record<string, string | undefined>, milestone) => {
      acc[milestone.type] = getMilestoneLabel(milestone);
      return acc;
    }, {});
  }, [account]);

  const exceptionInformation = account?.systemDesign?.toleranceException;
  const exception = account?.milestones?.[account?.milestones?.length - 1].exception;

  const internalException = exception;

  return (
    <Card className={className} position="relative">
      <Box
        padding="lg"
        childGap="md"
        direction={{ base: 'column', tablet: 'row' }}
        justifyContent="flex-start"
        alignItems="stretch"
      >
        <Box
          childGap="md"
          padding="2xs xs 0 0"
          direction="column"
          alignItems="flex-start"
          justifyContent="space-between"
          width={{ base: '100', tablet: '20' }}
        >
          <Box alignItems="flex-start" gap="2xs">
            <Box as="h3" fontWeight="medium" fontSize="md" direction="row" gap="sm">
              Progress Tracker
              {account?.cancellation?.isCancelled && <Badge message="cancelled" variant="danger" />}
              {hasUnsatisfiedPausingStipulations && <Badge message="stipulation" variant="warning" />}
            </Box>
            <Box
              display="inline-block"
              as="button"
              type="button"
              borderWidth="0"
              background="transparent"
              color="body-primary"
              padding="2xs 0"
              fontSize="xs"
              cursor="pointer"
              hover={{ color: 'contrast' }}
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? 'Hide' : 'Show'} Details
            </Box>
          </Box>
          {showDetails && !(accountIsHvac || accountIsWaterHeater || accountIsGenerator) && <ProjectDates />}
        </Box>
        <Box as="ul" flex="auto" padding="0" direction={{ base: 'column', tablet: 'row' }}>
          {isLoading && (
            <Box flex="auto" justifyContent="center">
              <Spinner size="lg" />
            </Box>
          )}
          {!isLoading &&
            sortedMilestones.map((milestone: Milestone, index: number, array: any[]) => {
              const isLast = index + 1 === array.length;
              const isFirst = index === 0;
              const sortedRequirements = milestone.requirements
                .filter(filterOptionalPendingRequirements)
                .toSorted((a: any, b: any) => a.order - b.order);
              const rejected = milestone.status === MilestoneStatus.rejected;
              const pending = milestone.status === MilestoneStatus.pending;
              const completed = Boolean(milestone?.completedAt).valueOf();
              return (
                <Box
                  flex="auto"
                  key={milestone?.type}
                  className={isLast ? '' : 'arrow-box'}
                  radius={isFirst ? 'sm 0 0 sm' : isLast ? '0 sm sm 0' : ''}
                  borderWidth={{ base: '0', tablet: isLast ? 'xs xs xs xs' : 'xs 0 xs xs' }}
                  padding={{ base: isLast ? 'sm 0 0 0' : 'sm 0', tablet: 'sm', desktop: 'md' }}
                  width={{ base: '100', tablet: '25' }}
                  gap="sm"
                  as="li"
                  borderColor="separator"
                >
                  {milestonePausingStipulations?.map((stipulation) => {
                    return stipulation?.accountMilestoneType === milestone?.type ? (
                      <PauseBanner
                        key={stipulation?.id}
                        stipulationType={stipulation?.stipulationType}
                        description={stipulation?.description}
                      />
                    ) : null;
                  })}
                  {milestone?.name === 'Install' && exceptionInformation && (
                    <Alert
                      variant="warning"
                      hasIcon
                      title="Production Tolerance Exception"
                      message={
                        <RequirePalmettoFinanceUser
                          children={`${exceptionInformation.note} - ${exceptionInformation.exceptionGivenBy}`}
                        />
                      }
                    />
                  )}
                  {milestone?.name === 'Activation' && internalException && (
                    <RequirePalmettoFinanceUser>
                      <Alert
                        variant="warning"
                        hasIcon
                        title="[INTERNAL] Exception Note"
                        message={`${internalException.note} - ${internalException.exceptionGivenBy}`}
                      />
                    </RequirePalmettoFinanceUser>
                  )}
                  <Box direction="row" childGap="xs" alignItems="center" position="relative">
                    {milestoneStatuses && <StatusIcon status={milestoneStatuses[milestone?.type]} />}
                    <Box flex="auto">
                      <Box
                        fontSize={{ base: 'sm', tablet: 'xs', desktop: 'sm' }}
                        fontWeight="medium"
                        style={{ lineHeight: 'var(--size-line-height-heading)' }}
                        data-intercom-target={intercomTarget('account', 'progress-tracker', milestone?.type)}
                      >
                        {milestone?.name}
                      </Box>
                    </Box>
                    <Box fontSize="sm" fontWeight="bold" color="grey-500">
                      {!completed && !pending && !rejected && milestoneLabels[milestone.type]}
                      {rejected && (
                        <Box as="span" color="danger-500">
                          {milestoneLabels[milestone.type]}
                        </Box>
                      )}
                      {milestone?.completedAt && (
                        <DateTimeDisplay value={milestone?.completedAt?.toString()} dateFormat={'DD'} excludeTime />
                      )}
                    </Box>
                  </Box>
                  {showDetails && (
                    <Box as="ul" padding="0 0 0 lg" childGap="xs" fontSize="xs">
                      {sortedRequirements
                        .toSorted((a: any, b: any) => a.order - b.order)
                        .map((req: any) => {
                          const requirements = req?.requirements || [];
                          const sortedSubRequirements = requirements
                            ?.filter(filterOptionalPendingRequirements)
                            .toSorted((a: any, b: any) => a.order - b.order);
                          return (
                            <SubTask
                              key={req.type}
                              status={req.status}
                              intercomTargetKey={intercomTarget(
                                'account',
                                'progress-tracker',
                                milestone?.type,
                                req.type,
                              )}
                            >
                              {req.name}
                              {sortedSubRequirements.length > 0 && (
                                <Box as="ul" padding="xs 0 0 0" childGap="xs" fontSize="xs">
                                  {sortedSubRequirements?.map((subReq: any) => (
                                    <SubTask
                                      key={subReq.type}
                                      status={subReq.status}
                                      intercomTargetKey={intercomTarget(
                                        'account',
                                        'progress-tracker',
                                        milestone?.type,
                                        req.type,
                                        subReq.type,
                                      )}
                                    >
                                      {subReq.name}
                                    </SubTask>
                                  ))}
                                </Box>
                              )}
                            </SubTask>
                          );
                        })}
                    </Box>
                  )}
                </Box>
              );
            })}
        </Box>
      </Box>
    </Card>
  );
}
